import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import classNames from "clsx";
import { Stepper, Step, StepLabel, styled } from "@mui/material";
import { TfiLayoutGrid2Alt } from "react-icons/tfi";
import { TbHomeSearch } from "react-icons/tb";
import { RiBarChart2Fill, RiFolderSettingsLine } from "react-icons/ri";
import { FaCubes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getUserType } from "../../../utils/authutils";

const CustomStepIconRoot = styled("div")(({ ownerState }) => ({
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: ownerState.active ? "#3D6C50" : ownerState.disabled ? "#b9bdc5" : "#D1F5C9",
  border: ownerState.active ? "2px solid #3D6C50" : "none",
  color: ownerState.active ? "#fff" : ownerState.disabled ? "#545960" : "#70C445",
  zIndex: 1,
  opacity: ownerState.disabled ? 0.5 : 1,
}));

const CustomStepLabel = styled(StepLabel)({
  "& .MuiStepLabel-label": {
    fontSize: "14px",
    color: "#9CA3AF",
    "&.Mui-active": {
      color: "#70C445",
      fontWeight: 500,
    },
    "&.Mui-completed": {
      color: "#fff",
      fontWeight: 500,
    },
    "&.Mui-disabled": {
      color: "#6B7280",
      opacity: 0.6,
    },
  },
});

export default function ReportSidebarLink({ sidebarLinks, isCollapsed, buildingParts }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pathname = location.pathname;

  const overviewImageId = searchParams.get("overviewImageId");
  const buildingId = searchParams.get("buildingId");
  const reportType = searchParams.get("reportType");
  const projectId = searchParams.get("projectId");

  const iconMap = {
    information: <TfiLayoutGrid2Alt size={12} />,
    custProperty: <TbHomeSearch size={14} />,
    "financial-overview": <RiBarChart2Fill size={14} />,
    maintenance: <RiFolderSettingsLine size={14} />,
  };

  const getStepFromPath = (path) => {
    if (path?.includes("/dashboard/building/information")) return "info";
    if (path?.includes("/dashboard/buildings/overview")) return "building";
    if (path?.includes("/dashboard/building/finance")) return "financial";
    if (path?.includes("/dashboard/building/maintainence")) return "maintanence";
    if (path?.startsWith("/dashboard/building/building-part/")) return "property";
    return searchParams.get("step");
  };

  const isLinkActive = (link) => {
    if (link.key === "dashboard") return pathname === "/dashboard" || pathname === "/dashboard/";
    return pathname === link.path || pathname.startsWith(link.path + "/");
  };

  const getDynamicPath = (link) => {
    const userType = getUserType();
    const step = getStepFromPath(link.path);
    if (
      (userType === "client" || userType === "client_member" || userType === "customer") &&
      link.key !== "projects" &&
      link.key !== "dashboard" &&
      link.key !== "managebids" &&
      link.key !== "document" &&
      link.key !== "damage-management"
    ) {
      return `${link.path}?step=${step || ""}&overviewImageId=${overviewImageId || ""}&buildingId=${buildingId || ""}&reportType=${reportType || ""}&projectId=${projectId || ""}`;
    }
    return link.path;
  };

  const flatSteps = [];
  sidebarLinks.forEach((link) => {
    const label =
      link.key === "custProperty"
        ? t("main.sidebar.building-overview", "Building Overview")
        : t(`main.sidebar.${link.key}`, link.label);

    flatSteps.push({ ...link, label, _isBuildingPart: false });

    if (link.key === "custProperty" && buildingParts?.length > 0) {
      buildingParts.forEach((part) => {
        flatSteps.push({
          key: `part-${part.id}`,
          label: t(`main.partialDetails.${part.name.toLowerCase()}`, part.name),
          path: `/dashboard/building/overview/building-part/${part.id}`,
          _partTo: `/dashboard/building/overview/building-part/${part.id}?overviewImageId=${overviewImageId || ""}&buildingId=${buildingId || ""}&partialImageId=${part.partialImageId || ""}&reportType=${reportType || ""}&projectId=${projectId || ""}`,
          icon: <FaCubes size={11} />,
          _isBuildingPart: true,
        });
      });
    }
  });

  return (
    <div
      className={classNames(
        "relative w-full mt-2",
        isCollapsed ? "flex flex-col items-center px-0" : "px-4",
      )}
    >
      <Stepper
        orientation="vertical"
        nonLinear
        activeStep={-1}
        connector={null}
        className="z-10 relative w-full"
        sx={
          isCollapsed
            ? {
                "& .MuiStep-root": {
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "20px",
                },
                "& .MuiStepLabel-root": { padding: 0 },
                "& .MuiStepLabel-iconContainer": { paddingRight: 0 },
              }
            : {}
        }
      >
        {flatSteps.map((item, idx) => {
          const active = pathname === item.path || pathname.startsWith(item.path + "/");
          const disabled = false;
          const dynamicPath = item._isBuildingPart ? item._partTo : getDynamicPath(item);

          return (
            <Step key={item.key || idx} completed={false} disabled={disabled}>
              <div className="cursor-pointer" onClick={() => navigate(dynamicPath)}>
                <CustomStepLabel
                  StepIconComponent={() => (
                    <CustomStepIconRoot ownerState={{ active, completed: false, disabled }}>
                      {iconMap[item.key] || item.icon}
                    </CustomStepIconRoot>
                  )}
                >
                  <span
                    className={classNames(
                      "flex items-center gap-1 transition-all duration-200 whitespace-nowrap overflow-hidden",
                      active ? "font-bold text-textSecondaryColor" : "",
                      !active && !disabled ? "text-textSecondaryColor/90" : "",
                      disabled ? "text-gray-600" : "",
                      item._isBuildingPart ? "text-xs" : "text-sm",
                      isCollapsed ? "max-w-0 opacity-0" : "max-w-[160px] opacity-100",
                    )}
                  >
                    {item.label}
                  </span>
                </CustomStepLabel>
              </div>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
}
